import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import styles from './MyMissions.module.css';
import { useToast } from '../context/ToastContext';
import { storeOrgPoints } from '../utils/rewardNotifications';
import { ClipboardList, Trophy } from 'lucide-react';

interface MissionAcceptance {
  id: number;
  missionId: number;
  missionTitle: string;
  missionObjective?: string;
  missionBriefing?: string;
  status: string;
  updatedAt: string;
  createdAt: string;
  notes?: string;
  rewardPoints?: number;
  bonusPoints?: number;
}

const MyMissions: React.FC = () => {
  const [missions, setMissions] = useState<MissionAcceptance[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMission, setSelectedMission] = useState<MissionAcceptance | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('InProgress');
  const [actionInProgress, setActionInProgress] = useState(false);
  const { showToast, showMissionCompleteToast } = useToast();
  const [orgPoints, setOrgPoints] = useState<number>(0);
  const previousMissionsRef = useRef<MissionAcceptance[]>([]);

  useEffect(() => {
    fetchMyMissions();
    fetchOrgPoints();
  }, []);

  useEffect(() => {
    // Check for newly completed missions (status changed from PendingVerification to Completed)
    if (previousMissionsRef.current.length > 0 && missions.length > 0) {
      missions.forEach((mission) => {
        const prevMission = previousMissionsRef.current.find(m => m.id === mission.id);
        if (prevMission && 
            prevMission.status === 'PendingVerification' && 
            mission.status === 'Completed' &&
            mission.rewardPoints) {
          // Mission just got verified! Show celebration
          const rewards = [
            `Mission "${mission.missionTitle}" verified!`,
            `Mission completion: ${mission.rewardPoints} points`
          ];
          showMissionCompleteToast(mission.rewardPoints, rewards, orgPoints);
        }
      });
    }
    // Update the reference for next comparison
    previousMissionsRef.current = missions;
  }, [missions, orgPoints, showMissionCompleteToast]);

  const fetchOrgPoints = async () => {
    try {
      const res = await api.get('/auth/user/org-points');
      const points = res.data.totalPoints || 0;
      setOrgPoints(points);
      // Update stored points to keep track of last known value
      storeOrgPoints(points);
    } catch (err) {
      console.error('Failed to fetch org points', err);
    }
  };

  const fetchMyMissions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/missions/my');
      setMissions(res.data || []);
      
      // Also refresh org points when missions are loaded
      await fetchOrgPoints();
    } catch (err) {
      console.error('Failed to fetch my missions', err);
      alert('Failed to load your missions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComplete = async (missionId: number) => {
    if (!window.confirm('Submit this mission as complete? It will be sent to leadership for verification.')) return;
    
    setActionInProgress(true);
    try {
      await api.post(`/missions/${missionId}/submit-complete`);
      showToast('✅ Mission submitted for verification! Leadership will review your completion shortly.', 'success');
      await fetchMyMissions();
      setIsModalOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.title || err?.response?.data || 'Failed to submit mission';
      showToast('❌ ' + msg, 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleReleaseMission = async (missionId: number) => {
    if (!window.confirm('Release this mission? You will be able to accept it again later.')) return;
    
    setActionInProgress(true);
    try {
      await api.post(`/missions/${missionId}/release`);
      showToast('Mission released successfully', 'info');
      await fetchMyMissions();
      setIsModalOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.title || err?.response?.data || 'Failed to release mission';
      showToast('❌ ' + msg, 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const handleFailMission = async (missionId: number) => {
    if (!window.confirm('Mark this mission as failed? This action cannot be undone.')) return;
    
    setActionInProgress(true);
    try {
      await api.post(`/missions/${missionId}/fail`);
      showToast('Mission marked as failed', 'warning');
      await fetchMyMissions();
      setIsModalOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.title || err?.response?.data || 'Failed to update mission';
      showToast('❌ ' + msg, 'error');
    } finally {
      setActionInProgress(false);
    }
  };

  const openMissionDetails = (mission: MissionAcceptance) => {
    setSelectedMission(mission);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMission(null);
  };

  const getStatusBadgeClass = (status: string) => {
    // Backend sends PascalCase enum names
    switch (status) {
      case 'InProgress': return styles.statusInProgress;
      case 'PendingVerification': return styles.statusPending;
      case 'Completed': return styles.statusCompleted;
      case 'Released': return styles.statusReleased;
      case 'Failed': return styles.statusFailed;
      default: return '';
    }
  };

  const getStatusLabel = (status: string) => {
    // Backend sends PascalCase enum names: InProgress, PendingVerification, etc.
    switch (status) {
      case 'InProgress': return 'IN PROGRESS';
      case 'PendingVerification': return 'PENDING VERIFICATION';
      case 'Completed': return 'COMPLETED';
      case 'Released': return 'RELEASED';
      case 'Failed': return 'FAILED';
      default: return status.toUpperCase();
    }
  };

  const filteredMissions = missions.filter(m => {
    if (statusFilter === 'all') return true;
    return m.status === statusFilter;
  });

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.card}>
          <div className={styles.loading}>Loading your missions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>My Missions</h1>
          <p className={styles.subtitle}>Track your accepted missions and submit completion reports</p>
        </div>

        <div className={styles.filterBar}>
          <select className={styles.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="all">All Missions</option>
            <option value="InProgress">In Progress</option>
            <option value="PendingVerification">Pending Verification</option>
            <option value="Completed">Completed</option>
            <option value="Released">Released</option>
            <option value="Failed">Failed</option>
          </select>
          <button className={styles.buttonGhost} onClick={fetchMyMissions}>Refresh</button>
        </div>

        {filteredMissions.length === 0 && (
          <div className={styles.emptyState}>
            {statusFilter === 'all' 
              ? 'You have not accepted any missions yet. Visit the Mission Board to get started!' 
              : `No missions with status: ${statusFilter}`}
          </div>
        )}

        <div className={styles.missionsList}>
          {filteredMissions.map(m => (
            <div key={m.id} className={styles.missionCard}>
              <div className={styles.missionBanner}>
                <span className={styles.missionIcon}><ClipboardList size={20} /></span>
                <span className={styles.missionType}>Mission Contract</span>
              </div>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{m.missionTitle}</h3>
                <span className={`${styles.statusBadge} ${getStatusBadgeClass(m.status)}`}>
                  {getStatusLabel(m.status)}
                </span>
              </div>
              <div className={styles.cardBody}>
                {m.missionObjective && (
                  <div className={styles.objectiveSection}>
                    <div className={styles.sectionLabel}>◆ OBJECTIVE</div>
                    <p className={styles.objectiveText}>{m.missionObjective}</p>
                  </div>
                )}
                {m.rewardPoints && m.rewardPoints > 0 && (
                  <div className={styles.rewardSection}>
                    <div>
                      <div className={styles.rewardLabel}><Trophy size={18} /> Mission Reward</div>
                    </div>
                    <div className={styles.rewardAmount}>
                      <span className={styles.rewardPoints}>{m.rewardPoints}</span>
                      <span className={styles.rewardUnit}>Points</span>
                    </div>
                  </div>
                )}
                <div className={styles.metaRow}>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Accepted:</span>
                    <span className={styles.metaValue}>{new Date(m.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Last Updated:</span>
                    <span className={styles.metaValue}>{new Date(m.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              <div className={styles.cardActions}>
                <button className={styles.buttonPrimary} onClick={() => openMissionDetails(m)}>
                  VIEW DETAILS & ACTIONS
                </button>
              </div>
            </div>
          ))}
        </div>

        {isModalOpen && selectedMission && (
          <div className={styles.modalOverlay} onClick={closeModal}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{selectedMission.missionTitle}</h3>
                <button className={styles.closeButton} onClick={closeModal}>×</button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.modalSection}>
                  <div className={styles.modalLabel}>STATUS</div>
                  <div className={`${styles.statusBadgeLarge} ${getStatusBadgeClass(selectedMission.status)}`}>
                    {getStatusLabel(selectedMission.status)}
                  </div>
                </div>
                {selectedMission.missionObjective && (
                  <div className={styles.modalSection}>
                    <div className={styles.modalLabel}>◆ MISSION OBJECTIVE</div>
                    <div className={styles.objectiveBox}>{selectedMission.missionObjective}</div>
                  </div>
                )}
                {selectedMission.missionBriefing && (
                  <div className={styles.modalSection}>
                    <div className={styles.modalLabel}>◆ MISSION BRIEFING</div>
                    <div 
                      className={styles.briefingBox} 
                      dangerouslySetInnerHTML={{ __html: selectedMission.missionBriefing }} 
                    />
                  </div>
                )}
                <div className={styles.modalSection}>
                  <div className={styles.modalLabel}>TIMELINE</div>
                  <div className={styles.timelineBox}>
                    <div>Accepted: {new Date(selectedMission.createdAt).toLocaleString()}</div>
                    <div>Last Updated: {new Date(selectedMission.updatedAt).toLocaleString()}</div>
                  </div>
                </div>
                {selectedMission.notes && (
                  <div className={styles.modalSection}>
                    <div className={styles.modalLabel}>NOTES</div>
                    <div className={styles.notesBox}>{selectedMission.notes}</div>
                  </div>
                )}
              </div>
              <div className={styles.modalFooter}>
                <div className={styles.actionButtons}>
                  {selectedMission.status.toLowerCase() === 'accepted' && (
                    <>
                      <button 
                        className={styles.buttonSuccess} 
                        onClick={() => handleSubmitComplete(selectedMission.missionId)}
                        disabled={actionInProgress}
                      >
                        ✓ SUBMIT AS COMPLETE
                      </button>
                      <button 
                        className={styles.buttonWarning} 
                        onClick={() => handleReleaseMission(selectedMission.missionId)}
                        disabled={actionInProgress}
                      >
                        ↩ RELEASE MISSION
                      </button>
                      <button 
                        className={styles.buttonDanger} 
                        onClick={() => handleFailMission(selectedMission.missionId)}
                        disabled={actionInProgress}
                      >
                        ✗ MARK AS FAILED
                      </button>
                    </>
                  )}
                  {selectedMission.status.toLowerCase() === 'inprogress' && (
                    <>
                      <button 
                        className={styles.buttonSuccess} 
                        onClick={() => handleSubmitComplete(selectedMission.missionId)}
                        disabled={actionInProgress}
                      >
                        ✓ SUBMIT AS COMPLETE
                      </button>
                      <button 
                        className={styles.buttonDanger} 
                        onClick={() => handleFailMission(selectedMission.missionId)}
                        disabled={actionInProgress}
                      >
                        ✗ MARK AS FAILED
                      </button>
                    </>
                  )}
                  {selectedMission.status.toLowerCase() === 'pendingverification' && (
                    <div className={styles.statusMessage}>
                      ⏳ Awaiting leadership verification. No actions available.
                    </div>
                  )}
                  {(selectedMission.status.toLowerCase() === 'completed' || 
                    selectedMission.status.toLowerCase() === 'released' || 
                    selectedMission.status.toLowerCase() === 'failed') && (
                    <div className={styles.statusMessage}>
                      This mission is closed. No actions available.
                    </div>
                  )}
                </div>
                <button className={styles.buttonGhost} onClick={closeModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyMissions;
